import { useParams } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatPercent } from '@/lib/money'
import { VAULTS } from './invest-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const RISK_LABEL = (risk: number) => (risk < 0.2 ? 'Low' : risk < 0.5 ? 'Medium' : 'High')

export function VaultDetailScreen() {
  const { id } = useParams()
  const vault = VAULTS.find((item) => item.id === id)
  if (!vault) return <NotFoundScreen />

  const isAssets = vault.id === 'assets'

  return (
    <>
      <PageHeader
        title={vault.name}
        subtitle={`${vault.rateBasis} · ${vault.term.toLowerCase()}`}
        actions={
          <>
            <Button variant="ghost" size="md" to="/invest">
              All products
            </Button>
            <Button
              variant="primary"
              size="md"
              iconEnd="arrow_forward"
              to={isAssets ? '/real-assets' : `/invest/${vault.id}/open`}
            >
              {isAssets ? 'See the buildings' : `Put money in`}
            </Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Stack direction="row" align="center" gap={3}>
                <Icon name={vault.icon} size={22} />
                <Text variant="caption" weight="semibold" tone="onDark">
                  {vault.name}
                </Text>
              </Stack>
              <Pill tone="accent">{formatPercent(vault.apy).replace('+', '')}</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-4">
              {(vault.apy * 100).toFixed(2)}%
            </Text>
            <Text variant="caption" tone="onDarkMuted">
              {vault.rateBasis}
            </Text>
            <div className={checkout.rule} />
            <Text variant="caption" tone="onDarkMuted">
              {vault.summary}
            </Text>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              The terms, plainly
            </Text>
            <Stack gap={0} className="pb-mt-2">
              {[
                ['Term', vault.term],
                ['Getting it back', vault.liquidity],
                ['Minimum', `$${vault.minimum.toLocaleString('en-US')}`],
                ['What it holds', vault.holds],
                ['Risk', RISK_LABEL(vault.risk)],
              ].map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold">
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
            <Card.Footer>
              <Stack gap={2}>
                <Meter value={vault.risk} height={6} tone="ink" label="Risk" />
                <Text variant="eyebrow" tone="secondary">
                  {vault.risk < 0.2
                    ? 'Cash at a regulated partner. The rate can fall, the balance does not.'
                    : vault.risk < 0.5
                      ? 'Backed by US Treasuries. Held to maturity, you get the stated yield.'
                      : 'Illiquid, not deposit-insured, and the value can fall. Only money you will not need.'}
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              How the rate is set
            </Text>
            <Text variant="caption" tone="secondary" className="pb-mt-3">
              {vault.id === 'vault'
                ? 'We never promise a fixed APY. What you earn is the index minus a spread, so when rates rise you earn more automatically, and when they fall we are not paying you out of our own pocket.'
                : vault.id === 'ladder'
                  ? 'Each rung is bought at the yield available on the day. The headline number is the blended yield of the ladder you hold right now, not a forecast.'
                  : 'Rent is collected locally and paid monthly after a 1.0% platform fee. If a building sits empty there is no rent that month — the yield is a target, not a promise.'}
            </Text>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
